/*
 * video Vue component
 * ---
 * Handles volume toggle and play/pause for hero video banners.
 * Unmutes and plays the video on click; mutes on second click.
 * Auto-switches active video on resize at 768px breakpoint.
 * Carries volume and play/pause state across breakpoints.
 *
 * Example usage:
 *
 * <video-banner inline-template>
 *   <div ref="desktopVideo">…</div>
 *   <div ref="mobileVideo">…</div>
 * </video-banner>
 */

const MOBILE_BREAKPOINT = 768;

export default {
    data() {
        return {
            desktopMuted: true,
            mobileMuted: true,
            desktopPlaying: true,
            mobilePlaying: true,
            isMobile: false,
        };
    },

    mounted() {
        this.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
        this._onResize = this.handleResize.bind(this);
        window.addEventListener('resize', this._onResize);

        // Ensure the initially hidden video is muted
        this.syncVideos();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this._onResize);
    },

    methods: {
        getVideo(ref) {
            const el = this.$refs[ref];
            return el ? el.querySelector('video') : null;
        },

        toggleVolume(target) {
            if (target === 'desktop') {
                this.desktopMuted = !this.desktopMuted;
                const v = this.getVideo('desktopVideo');
                if (v) {
                    v.muted = this.desktopMuted;
                    if (!this.desktopMuted) v.play();
                }
            } else if (target === 'mobile') {
                this.mobileMuted = !this.mobileMuted;
                const v = this.getVideo('mobileVideo');
                if (v) {
                    v.muted = this.mobileMuted;
                    if (!this.mobileMuted) v.play();
                }
            }
        },

        togglePlayPause(target) {
            if (target === 'desktop') {
                const v = this.getVideo('desktopVideo');
                if (v) {
                    if (this.desktopPlaying) {
                        v.pause();
                    } else {
                        v.play();
                    }
                    this.desktopPlaying = !this.desktopPlaying;
                }
            } else if (target === 'mobile') {
                const v = this.getVideo('mobileVideo');
                if (v) {
                    if (this.mobilePlaying) {
                        v.pause();
                    } else {
                        v.play();
                    }
                    this.mobilePlaying = !this.mobilePlaying;
                }
            }
        },

        handleResize() {
            const wasMobile = this.isMobile;
            this.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

            if (wasMobile === this.isMobile) return; // no breakpoint change

            if (this.isMobile) {
                // Desktop → Mobile: carry desktop state to mobile
                this.mobileMuted = this.desktopMuted;
                this.mobilePlaying = this.desktopPlaying;
            } else {
                // Mobile → Desktop: carry mobile state to desktop
                this.desktopMuted = this.mobileMuted;
                this.desktopPlaying = this.mobilePlaying;
            }

            this.syncVideos();
        },

        syncVideos() {
            const desktopV = this.getVideo('desktopVideo');
            const mobileV = this.getVideo('mobileVideo');

            if (this.isMobile) {
                // Mobile is active
                if (mobileV) {
                    mobileV.muted = this.mobileMuted;
                    if (this.mobilePlaying) {
                        mobileV.play();
                    } else {
                        mobileV.pause();
                    }
                }
                // Always pause & mute the hidden desktop video
                if (desktopV) {
                    desktopV.muted = true;
                    desktopV.pause();
                }
            } else {
                // Desktop is active
                if (desktopV) {
                    desktopV.muted = this.desktopMuted;
                    if (this.desktopPlaying) {
                        desktopV.play();
                    } else {
                        desktopV.pause();
                    }
                }
                // Always pause & mute the hidden mobile video
                if (mobileV) {
                    mobileV.muted = true;
                    mobileV.pause();
                }
            }
        },
    },
};